import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  Plus,
  Pencil,
  Trash2,
  Server,
  Globe,
  Lock,
  RefreshCw,
  CheckCircle2,
  Circle,
  ShieldCheck,
  Eye,
  BarChart3,
  ChevronRight,
} from 'lucide-react';
import { api, ApiError } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';
import { useEntitlement } from '../hooks/useEntitlement.js';
import { PlanUpgradeNotice } from '../components/PlanGuard.js';
import type {
  EmailSender,
  EmailSenderType,
  CreateEmailSenderInput,
  UpdateEmailSenderInput,
} from '@blogforge/shared';
import { formatDate } from '../lib/billing-format.js';

// ─────────────────────────────────────────────────────────────────
// Email senders — stored delivery identities (SMTP / Brevo API) for
// the ACTIVE workspace. Owners/admins create & manage senders; every
// member can view (and use) them when sending. Creation is capped by
// plan (free: 1 sender).
// ─────────────────────────────────────────────────────────────────

interface SenderForm {
  name: string;
  description: string;
  senderType: EmailSenderType;
  // SMTP
  host: string;
  port: string;
  secure: boolean;
  user: string;
  pass: string;
  from: string;
  fromName: string;
  // Brevo
  apiKey: string;
  brevoFromEmail: string;
  brevoFromName: string;
}

const EMPTY_FORM: SenderForm = {
  name: '',
  description: '',
  senderType: 'smtp',
  host: '',
  port: '587',
  secure: false,
  user: '',
  pass: '',
  from: '',
  fromName: '',
  apiKey: '',
  brevoFromEmail: '',
  brevoFromName: '',
};

export function EmailSendersPage() {
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const qc = useQueryClient();
  const entitlement = useEntitlement();

  // Caller's role in the active workspace (owner/admin manage senders).
  const { data: workspacesData } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api.workspaces.list(),
  });
  const workspace = workspacesData?.workspaces.find((w) => w.id === activeWorkspaceId);
  const canManage = workspace?.role === 'owner' || workspace?.role === 'admin';

  const sendersQuery = useQuery({
    queryKey: ['email-senders', activeWorkspaceId],
    queryFn: () => api.emailSenders.list(),
    enabled: Boolean(activeWorkspaceId),
  });
  const senders = sendersQuery.data?.data ?? [];

  const atSenderLimit =
    entitlement.data !== undefined &&
    entitlement.data.limits.maxSenders !== null &&
    (entitlement.data.usage.senders ?? 0) >= (entitlement.data.limits.maxSenders as number);

  const [modal, setModal] = useState<
    null | { mode: 'create' } | { mode: 'edit'; sender: EmailSender }
  >(null);
  const [form, setForm] = useState<SenderForm>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['email-senders', activeWorkspaceId] });
    void qc.invalidateQueries({ queryKey: ['billing', 'entitlement', activeWorkspaceId] });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (modal?.mode === 'edit') {
        const payload = buildUpdatePayload(form, modal.sender.isActive);
        return api.emailSenders.update(modal.sender.id, payload);
      }
      return api.emailSenders.create(buildCreatePayload(form));
    },
    onSuccess: () => {
      setModal(null);
      setForm(EMPTY_FORM);
      invalidate();
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err),
      );
    },
  });

  const toggleActive = async (sender: EmailSender, next: boolean) => {
    try {
      const payload: UpdateEmailSenderInput =
        sender.senderType === 'smtp'
          ? { senderType: 'smtp', isActive: next }
          : { senderType: 'brevo_api', isActive: next };
      await api.emailSenders.update(sender.id, payload);
      invalidate();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const removeSender = async (sender: EmailSender) => {
    if (!confirm(`Delete sender "${sender.name}"? Emails can no longer be sent from it.`)) return;
    try {
      await api.emailSenders.remove(sender.id);
      invalidate();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const openCreate = () => {
    setError(null);
    setForm(EMPTY_FORM);
    setModal({ mode: 'create' });
  };

  const openEdit = (sender: EmailSender) => {
    setError(null);
    const data = (sender.data ?? {}) as Record<string, unknown>;
    setForm({
      name: sender.name,
      description: sender.description ?? '',
      senderType: sender.senderType,
      host: String(data.host ?? ''),
      port: String(data.port ?? 587),
      secure: Boolean(data.secure),
      user: String(data.user ?? ''),
      pass: '',
      from: String(data.from ?? ''),
      fromName: String(data.fromName ?? ''),
      apiKey: '',
      brevoFromEmail: String(data.fromEmail ?? ''),
      brevoFromName: String(data.fromName ?? ''),
    });
    setModal({ mode: 'edit', sender });
  };

  const addBlockedReason = atSenderLimit
    ? `The ${entitlement.data?.plan ?? 'free'} plan allows up to ${entitlement.data?.limits.maxSenders} sender(s) — upgrade to add more.`
    : !canManage
      ? 'Only owners and admins can add senders.'
      : undefined;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Page header ─────────────────────────────────────── */}
      <header className="relative shrink-0 px-8 pt-8 pb-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="kicker mb-2">
              <span className="bg-accent h-1.5 w-1.5 rounded-full" />
              Email
            </p>
            <h1 className="text-ink text-[22px] font-bold tracking-tight">Senders</h1>
            <p className="text-ink-soft mt-1 text-[13px]">
              Saved SMTP &amp; Brevo identities used to deliver your emails. Members can use them;
              owners and admins manage them.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!canManage && (
              <span className="border-line bg-bg/60 text-muted flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11.5px] font-semibold">
                <Eye size={12} />
                View only
              </span>
            )}
            <button
              type="button"
              onClick={openCreate}
              disabled={!canManage || atSenderLimit || saveMutation.isPending}
              title={addBlockedReason}
              className="btn-primary"
            >
              {canManage && !atSenderLimit ? (
                <>
                  <Plus size={15} strokeWidth={2.4} />
                  Add sender
                </>
              ) : (
                <>
                  <Lock size={13} />
                  Add sender
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ── Plan / role notice ───────────────────────────────── */}
      {addBlockedReason && !atSenderLimit && (
        <div className="shrink-0 px-8 pb-4">
          <div className="border-line bg-bg/60 text-muted flex items-center gap-2 rounded-lg border px-3 py-2 text-[12px]">
            <ShieldCheck size={13} className="text-accent" />
            {addBlockedReason}
          </div>
        </div>
      )}
      {atSenderLimit && (
        <div className="shrink-0 px-8 pb-4">
          <PlanUpgradeNotice reason={addBlockedReason ?? 'Sender limit reached.'} />
        </div>
      )}

      {error && (
        <div className="shrink-0 px-8 pb-3">
          <p className="border-danger/20 bg-danger/8 text-danger rounded-lg border px-3 py-2 text-[12.5px]">
            {error}
          </p>
        </div>
      )}

      {/* ── Sender list ─────────────────────────────────────── */}
      <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-10">
        {sendersQuery.isLoading ? (
          <div className="text-muted flex h-48 items-center justify-center gap-2 text-sm">
            <RefreshCw size={14} className="animate-spin" />
            Loading senders…
          </div>
        ) : senders.length === 0 ? (
          <div className="flex h-full min-h-72 flex-col items-center justify-center text-center">
            <div className="bg-accent/10 text-accent mb-5 flex h-16 w-16 items-center justify-center rounded-2xl shadow-[inset_0_0_0_1px_var(--color-accent-border)]">
              <Server size={26} strokeWidth={1.8} />
            </div>
            <h2 className="text-ink text-base font-semibold">No senders configured yet</h2>
            <p className="text-muted mt-1 max-w-sm text-[13px]">
              Connect an SMTP server (Gmail, Postmark, Mailgun…) or a Brevo account so your emails
              have a verified “From” identity to go out on.
            </p>
            {canManage && !atSenderLimit && (
              <button type="button" onClick={openCreate} className="btn-primary mt-5">
                <Plus size={15} strokeWidth={2.4} />
                Add your first sender
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2.5">
            {senders.map((sender) => (
              <SenderRow
                key={sender.id}
                sender={sender}
                canManage={canManage}
                onEdit={() => openEdit(sender)}
                onToggleActive={(next) => void toggleActive(sender, next)}
                onRemove={() => void removeSender(sender)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Create / edit modal ─────────────────────────────── */}
      {modal && (
        <SenderModal
          mode={modal.mode}
          sender={modal.mode === 'edit' ? modal.sender : null}
          form={form}
          setForm={setForm}
          busy={saveMutation.isPending}
          error={error}
          onClose={() => setModal(null)}
          onSubmit={() => saveMutation.mutate()}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Payload builders (exactOptionalPropertyTypes-safe)
// ─────────────────────────────────────────────────────────────────
function buildSmtpData(form: SenderForm) {
  return {
    host: form.host,
    port: Number(form.port),
    secure: form.secure,
    ...(form.user ? { user: form.user } : {}),
    // Blank / absent secret keeps the stored value on update.
    ...(form.pass ? { pass: form.pass } : {}),
    ...(form.from ? { from: form.from } : {}),
    ...(form.fromName ? { fromName: form.fromName } : {}),
  };
}

function buildBrevoData(form: SenderForm) {
  return {
    ...(form.apiKey ? { apiKey: form.apiKey } : {}),
    ...(form.brevoFromEmail ? { fromEmail: form.brevoFromEmail } : {}),
    ...(form.brevoFromName ? { fromName: form.brevoFromName } : {}),
  };
}

function buildCreatePayload(form: SenderForm): CreateEmailSenderInput {
  if (form.senderType === 'brevo_api') {
    return {
      name: form.name,
      ...(form.description ? { description: form.description } : {}),
      senderType: 'brevo_api',
      data: {
        apiKey: form.apiKey,
        fromEmail: form.brevoFromEmail,
        ...(form.brevoFromName ? { fromName: form.brevoFromName } : {}),
      },
    };
  }
  return {
    name: form.name,
    ...(form.description ? { description: form.description } : {}),
    senderType: 'smtp',
    data: buildSmtpData(form),
  };
}

function buildUpdatePayload(form: SenderForm, isActive: boolean): UpdateEmailSenderInput {
  if (form.senderType === 'brevo_api') {
    return {
      ...(form.name ? { name: form.name } : {}),
      ...(form.description !== undefined ? { description: form.description || null } : {}),
      senderType: 'brevo_api',
      isActive,
      ...(form.apiKey || form.brevoFromEmail || form.brevoFromName
        ? { data: buildBrevoData(form) }
        : {}),
    };
  }
  return {
    ...(form.name ? { name: form.name } : {}),
    ...(form.description !== undefined ? { description: form.description || null } : {}),
    senderType: 'smtp',
    isActive,
    ...(form.host || form.port || form.user || form.pass || form.from || form.fromName
      ? { data: buildSmtpData(form) }
      : {}),
  };
}

// ─────────────────────────────────────────────────────────────────
// Row
// ─────────────────────────────────────────────────────────────────
export function SenderRow({
  sender,
  canManage,
  onEdit,
  onToggleActive,
  onRemove,
}: {
  sender: EmailSender;
  canManage: boolean;
  onEdit: () => void;
  onToggleActive: (next: boolean) => void;
  onRemove: () => void;
}) {
  const data = (sender.data ?? {}) as Record<string, unknown>;
  const isSmtp = sender.senderType === 'smtp';
  const endpoint = isSmtp
    ? [String(data.host ?? ''), String(data.from ?? '')].filter(Boolean).join(' · ')
    : String(data.fromEmail ?? '');

  return (
    <div className="group surface-row hover:border-accent-border hover:shadow-glow flex items-center gap-4 px-4 py-3.5 transition-all duration-150">
      {/* The row opens the sender's view page — metrics, domain verification and
          mail health. It is a link rather than a click handler so it stays
          keyboard-reachable, and the action buttons beside it never navigate. */}
      <Link
        to={`/emails/senders/${sender.id}`}
        title={`Open ${sender.name} — metrics, domain verification and mail health`}
        className="flex min-w-0 flex-1 items-center gap-4"
      >
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors ${
            sender.isActive
              ? 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]'
              : 'bg-bg text-muted group-hover:text-accent'
          }`}
        >
          {isSmtp ? <Server size={15} /> : <Globe size={15} />}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <p className="text-ink group-hover:text-accent truncate text-sm font-semibold transition-colors">
              {sender.name}
            </p>
            <span className="bg-ink/5 text-muted rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase">
              {sender.senderType === 'smtp' ? 'SMTP' : 'Brevo API'}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${
                sender.isActive ? 'bg-ok/10 text-ok' : 'bg-ink/5 text-muted'
              }`}
              title={sender.isActive ? 'Active sender' : 'Inactive'}
            >
              {sender.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          {sender.description ? (
            <p className="text-muted mt-0.5 truncate text-xs">{sender.description}</p>
          ) : null}
          <p className="text-muted mt-1 truncate text-[11.5px]">
            {endpoint || '—'}
            {sender.hasSecret ? '' : ' · no credentials stored'}
            {sender.lastUsedAt ? ` · used ${formatDate(sender.lastUsedAt)}` : ' · never used'}
          </p>
        </div>
      </Link>

      {/* Managers get the labelled View button in the hover cluster; anyone
          else gets a quiet chevron saying the row opens something. */}
      {!canManage && (
        <Link
          to={`/emails/senders/${sender.id}`}
          aria-label={`View ${sender.name}`}
          title="Metrics, domain verification and mail health"
          className="text-muted group-hover:text-ink-soft shrink-0 transition-colors"
        >
          <ChevronRight size={15} />
        </Link>
      )}

      {canManage && (
        <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          <Link
            to={`/emails/senders/${sender.id}`}
            className="btn-ghost px-2.5 text-xs"
            title="Metrics, domain verification and mail health"
          >
            <BarChart3 size={13} />
            View
          </Link>
          <button
            type="button"
            onClick={() => onToggleActive(!sender.isActive)}
            className="btn-ghost px-2.5 text-xs"
            title={
              sender.isActive
                ? 'Deactivate (stop using for new sends)'
                : 'Activate as sending identity'
            }
          >
            {sender.isActive ? <Circle size={13} /> : <CheckCircle2 size={13} />}
            {sender.isActive ? 'Deactivate' : 'Activate'}
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="btn-ghost px-2.5 text-xs"
            title="Edit sender"
          >
            <Pencil size={13} />
            Edit
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="btn-ghost text-danger hover:bg-danger/10 hover:text-danger px-2.5 text-xs"
            title="Delete sender"
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Modal
// ─────────────────────────────────────────────────────────────────
function SenderModal({
  mode,
  sender,
  form,
  setForm,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  mode: 'create' | 'edit';
  sender: EmailSender | null;
  form: SenderForm;
  setForm: React.Dispatch<React.SetStateAction<SenderForm>>;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const set = <K extends keyof SenderForm>(key: K, value: SenderForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const inputCls = 'field-input w-full';
  const label = (text: string, extra?: string) => (
    <label className={`field-label mt-3 block ${extra ?? ''}`}>{text}</label>
  );

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div className="modal-card w-full max-w-xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-ink text-[17px] font-bold tracking-tight">
            {mode === 'create' ? 'Add email sender' : `Edit ${sender?.name ?? 'sender'}`}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-muted hover:bg-card-2 hover:text-ink-soft rounded p-1 disabled:opacity-40"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <p className="text-ink-soft mb-4 text-sm">
          {mode === 'edit' && sender?.hasSecret
            ? 'Leave the secret field blank to keep the stored one.'
            : 'Credentials are stored encrypted server-side and never shown again.'}
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className="space-y-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              {label('Name')}
              <input
                className={inputCls}
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="e.g. Product updates"
                required
                autoFocus
              />
            </div>
            <div>
              {label('Description')}
              <input
                className={inputCls}
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Type selector */}
          <div>
            {label('Sender type')}
            <div className="grid grid-cols-2 gap-2">
              {(['smtp', 'brevo_api'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  disabled={mode === 'edit'}
                  onClick={() => set('senderType', type)}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-[13px] font-semibold transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-60 ${
                    form.senderType === type
                      ? 'border-accent-border bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]'
                      : 'border-line bg-bg/40 text-ink-soft hover:border-accent-border'
                  }`}
                >
                  {type === 'smtp' ? <Server size={15} /> : <Globe size={15} />}
                  <span>
                    {type === 'smtp' ? 'SMTP' : 'Brevo API'}
                    <span className="text-muted block text-[11px] font-medium">
                      {type === 'smtp' ? 'Any SMTP provider' : 'Your Brevo account key'}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          {form.senderType === 'smtp' ? (
            <>
              <div className="grid grid-cols-[1fr_110px] gap-2">
                <div>
                  {label('SMTP host')}
                  <input
                    className={inputCls}
                    value={form.host}
                    onChange={(e) => set('host', e.target.value)}
                    placeholder="smtp.example.com"
                    required
                  />
                </div>
                <div>
                  {label('Port')}
                  <input
                    type="number"
                    className={inputCls}
                    value={form.port}
                    onChange={(e) => set('port', e.target.value)}
                    min={1}
                    max={65535}
                  />
                </div>
              </div>
              <label className="mt-2 flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.secure}
                  onChange={(e) => set('secure', e.target.checked)}
                  className="border-line-strong rounded"
                />
                <span className="text-ink-soft text-xs">
                  Use SSL/TLS (implicit TLS, usually port 465)
                </span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  {label('Username')}
                  <input
                    className={inputCls}
                    value={form.user}
                    onChange={(e) => set('user', e.target.value)}
                    placeholder="Optional"
                    autoComplete="username"
                  />
                </div>
                <div>
                  {label('Password')}
                  <input
                    type="password"
                    className={inputCls}
                    value={form.pass}
                    onChange={(e) => set('pass', e.target.value)}
                    placeholder={
                      mode === 'edit' && sender?.hasSecret ? '•••••••• (unchanged)' : 'Optional'
                    }
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  {label('From address')}
                  <input
                    type="email"
                    className={inputCls}
                    value={form.from}
                    onChange={(e) => set('from', e.target.value)}
                    placeholder="hello@example.com"
                  />
                </div>
                <div>
                  {label('From name')}
                  <input
                    className={inputCls}
                    value={form.fromName}
                    onChange={(e) => set('fromName', e.target.value)}
                    placeholder="e.g. BlogForger"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                {label('Brevo API key')}
                <input
                  type="password"
                  className={inputCls}
                  value={form.apiKey}
                  onChange={(e) => set('apiKey', e.target.value)}
                  placeholder={
                    mode === 'edit' && sender?.hasSecret ? '•••••••• (unchanged)' : 'xkeysib-…'
                  }
                  autoComplete="new-password"
                  required={mode === 'create'}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  {label('From email')}
                  <input
                    type="email"
                    className={inputCls}
                    value={form.brevoFromEmail}
                    onChange={(e) => set('brevoFromEmail', e.target.value)}
                    placeholder="hello@example.com"
                    required={mode === 'create'}
                  />
                </div>
                <div>
                  {label('From name')}
                  <input
                    className={inputCls}
                    value={form.brevoFromName}
                    onChange={(e) => set('brevoFromName', e.target.value)}
                    placeholder="e.g. BlogForger"
                  />
                </div>
              </div>
            </>
          )}

          {error && (
            <p className="border-danger/20 bg-danger/8 text-danger rounded-lg border px-3 py-2 text-[12.5px]">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="btn-secondary rounded-lg px-4 py-2 text-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button type="submit" disabled={busy} className="btn-primary disabled:opacity-50">
              {busy ? 'Saving…' : mode === 'create' ? 'Add sender' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
